import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getFeaturedPost } from '../config/featuredBlogPosts';
import { WELLNESS_LEGAL_DISCLAIMER } from '../config/wellnessDisclaimer';
import { formatBlogDate } from '../utils/blogUtils';
import { renderBlogRichText } from '../utils/blogRichText';
import BlogShareBar from './BlogShareBar';
import BoatRaceLanguageSwitcher from './BoatRaceLanguageSwitcher';
import FormationDiagram from './FormationDiagram';
import { getCategoryById, resolvePostBlogCategory } from '../config/blogCategories';
import { isBoatRacePost } from '../config/boatRacePostUtils';
import { useBlogPostMeta } from '../hooks/useBlogPostMeta';
import './WellnessGuideBlogArticle.css';

function WellnessLegalDisclaimerModal({ open, onClose }) {
  const titleId = useId();
  const closeRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="wellness-disclaimer-modal__overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="wellness-disclaimer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="wellness-disclaimer-modal__header">
          <h2 id={titleId}>Important — not medical advice</h2>
          <button
            ref={closeRef}
            type="button"
            className="wellness-disclaimer-modal__close"
            onClick={onClose}
            aria-label="Close disclaimer"
          >
            ×
          </button>
        </div>
        <div className="wellness-disclaimer-modal__body">
          {WELLNESS_LEGAL_DISCLAIMER.map((paragraph) => (
            <p key={paragraph.slice(0, 48)}>{paragraph}</p>
          ))}
        </div>
        <div className="wellness-disclaimer-modal__footer">
          <button type="button" className="wellness-disclaimer-modal__done" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const renderParagraphs = (paragraphs = []) =>
  paragraphs.map((text, index) => (
    <p key={index}>{renderBlogRichText(text)}</p>
  ));

const renderCallout = (callout) => {
  if (!callout) return null;
  if (typeof callout === 'object' && callout.url) {
    const label = callout.text || callout.label || callout.url;
    const isInternal = callout.url.startsWith('/');
    return (
      <div className="wellness-article__callout">
        {isInternal ? (
          <Link to={callout.url}>{renderBlogRichText(label)}</Link>
        ) : (
          <a href={callout.url} target="_blank" rel="noopener noreferrer">
            {renderBlogRichText(label)}
          </a>
        )}
      </div>
    );
  }
  return <div className="wellness-article__callout">{renderBlogRichText(callout)}</div>;
};

export const renderWellnessSection = (section, index) => {
  switch (section.type) {
    case 'stats':
      return (
        <section key={index} className="wellness-article__stats" aria-label={section.ariaLabel}>
          {section.items.map((item) => (
            <div key={item.label} className="wellness-article__stat-card">
              <span className="wellness-article__stat-value">{item.value}</span>
              <span className="wellness-article__stat-label">{item.label}</span>
            </div>
          ))}
        </section>
      );
    case 'panel':
      return (
        <section key={index} className="wellness-article__panel">
          {section.title && <h2>{section.title}</h2>}
          {section.paragraphs && renderParagraphs(section.paragraphs)}
          {section.list && (
            <ul>
              {section.list.map((item) => (
                <li key={item}>{renderBlogRichText(item)}</li>
              ))}
            </ul>
          )}
          {section.orderedList && (
            <ol>
              {section.orderedList.map((item) => (
                <li key={item}>{renderBlogRichText(item)}</li>
              ))}
            </ol>
          )}
          {section.chips && (
            <div className="wellness-article__chip-row">
              {section.chips.map((chip) => (
                <span key={chip} className="wellness-article__chip">
                  {chip}
                </span>
              ))}
            </div>
          )}
          {section.callout && renderCallout(section.callout)}
        </section>
      );
    case 'table':
      return (
        <section key={index} className="wellness-article__panel">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          <div className="wellness-article__table-wrap">
            <table className="wellness-article__table">
              <thead>
                <tr>
                  {section.headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <tr key={row.join('|')}>
                    {row.map((cell, cellIndex) => (
                      <td key={`${cellIndex}-${cell}`}>{renderBlogRichText(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    case 'hubLinks':
      return (
        <section key={index} className="wellness-article__panel wellness-article__hub">
          {section.title && <h2>{section.title}</h2>}
          {section.groups?.map((group) => (
            <div key={group.title} className="wellness-article__hub-group">
              <h3>{group.title}</h3>
              <ul className="wellness-article__hub-list">
                {group.links.map((link) => (
                  <li key={link.slug}>
                    <Link to={`/blog/${link.slug}`}>{link.label}</Link>
                    {link.meta && <span className="wellness-article__hub-meta"> — {link.meta}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      );
    case 'videoList':
      return (
        <section key={index} className="wellness-article__panel wellness-article__videos">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          <ul className="wellness-article__video-list">
            {section.videos.map((video) => (
              <li key={video.url} className="wellness-article__video-card">
                <h3>{video.title}</h3>
                {video.channel && (
                  <p className="wellness-article__video-channel">{video.channel}</p>
                )}
                {video.description && <p>{renderBlogRichText(video.description)}</p>}
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wellness-article__video-link"
                >
                  Watch on YouTube →
                </a>
              </li>
            ))}
          </ul>
        </section>
      );
    case 'videoEmbed':
      return (
        <section key={index} className="wellness-article__panel wellness-article__video-embeds">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          {section.embeds.map((embed) => (
            <div key={embed.youtubeId} className="wellness-article__embed-block">
              <div className="wellness-article__embed-wrap">
                <iframe
                  title={embed.title}
                  src={`https://www.youtube.com/embed/${embed.youtubeId}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              {embed.caption && (
                <p className="wellness-article__embed-caption">{renderBlogRichText(embed.caption)}</p>
              )}
            </div>
          ))}
        </section>
      );
    case 'imageGallery': {
      const layout = ['hero', 'portrait', 'video'].includes(section.layout)
        ? section.layout
        : null;
      const gridClass = layout
        ? `wellness-article__gallery-grid wellness-article__gallery-grid--${layout}`
        : 'wellness-article__gallery-grid';
      return (
        <section key={index} className="wellness-article__panel wellness-article__gallery">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          <div className={gridClass}>
            {section.images.map((image) => {
              const isVideo =
                image.kind === 'video' || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(image.url || '');
              return (
                <figure key={image.url} className="wellness-article__gallery-item">
                  {isVideo ? (
                    <video
                      src={image.url}
                      poster={image.poster}
                      controls
                      playsInline
                      preload="metadata"
                      title={image.alt || 'Hike video'}
                    >
                      {image.alt || 'Hike video'}
                    </video>
                  ) : (
                    <img src={image.url} alt={image.alt} loading="lazy" />
                  )}
                  {(image.caption || image.mapUrl) && (
                    <figcaption>
                      {image.caption && renderBlogRichText(image.caption)}
                      {image.mapUrl && (
                        <a
                          className="wellness-article__gallery-map"
                          href={image.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open trailhead map →
                        </a>
                      )}
                    </figcaption>
                  )}
                </figure>
              );
            })}
          </div>
        </section>
      );
    }
    case 'mapEmbed':
      return (
        <section key={index} className="wellness-article__panel wellness-article__maps">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          <div className="wellness-article__maps-grid">
            {section.maps.map((map) => (
              <div key={map.title} className="wellness-article__map-card">
                {map.title && <h3>{map.title}</h3>}
                {map.address && (
                  <p className="wellness-article__map-address">{renderBlogRichText(map.address)}</p>
                )}
                <div className="wellness-article__embed-wrap wellness-article__embed-wrap--map">
                  <iframe
                    title={map.title || 'Trailhead map'}
                    src={map.embedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                {map.directionsUrl && (
                  <p className="wellness-article__embed-caption">
                    <a href={map.directionsUrl} target="_blank" rel="noopener noreferrer">
                      Open in Google Maps →
                    </a>
                    {map.caption ? ` ${map.caption}` : ''}
                  </p>
                )}
                {!map.directionsUrl && map.caption && (
                  <p className="wellness-article__embed-caption">{renderBlogRichText(map.caption)}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      );
    case 'formationDiagram':
      return (
        <section key={index} className="wellness-article__panel">
          <FormationDiagram
            title={section.title}
            caption={section.caption}
            legend={section.legend}
            players={section.players}
            opponents={section.opponents}
            arrows={section.arrows}
            animated={section.animated}
          />
        </section>
      );
    case 'dayBlocks':
      return (
        <section key={index} className="wellness-article__panel">
          {section.title && <h2>{section.title}</h2>}
          {section.intro && renderParagraphs([section.intro])}
          {section.days.map((day) => (
            <div key={day.title}>
              <h3>{day.title}</h3>
              <ul>
                {day.meals.map((meal) => {
                  const colon = meal.indexOf(':');
                  if (colon === -1) {
                    return <li key={meal}>{renderBlogRichText(meal)}</li>;
                  }
                  return (
                    <li key={meal}>
                      <strong>{meal.slice(0, colon + 1)}</strong>
                      {renderBlogRichText(meal.slice(colon + 1))}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      );
    default:
      return null;
  }
};

const WellnessGuideBlogArticle = () => {
  const { slug } = useParams();
  const post = getFeaturedPost(slug);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useBlogPostMeta(post);

  if (!post || post.component !== 'WellnessGuideBlogArticle') {
    return (
      <div className="blog-page">
        <p className="blog-page__status">Post not found</p>
      </div>
    );
  }

  const themeClass = post.theme === 'exercise' ? 'wellness-article--exercise' : '';
  const postCategory = resolvePostBlogCategory(post);
  const categoryNav = getCategoryById(postCategory);

  return (
    <article className={`wellness-article ${themeClass}`}>
      <header className="wellness-article__hero">
        <div
          className="wellness-article__hero-bg"
          style={{ backgroundImage: `url(${post.coverImageUrl})` }}
          aria-hidden="true"
        />
        <div className="wellness-article__hero-scrim" aria-hidden="true" />
        <div className="wellness-article__hero-content">
          <Link to="/blog" className="wellness-article__back">
            ← Back to blog
          </Link>
          {categoryNav && postCategory !== 'all' && (
            <Link to={categoryNav.path} className="wellness-article__category">
              {categoryNav.label}
            </Link>
          )}
          <p className="wellness-article__kicker">
            <span className="wellness-article__badge">Featured</span>
            {post.audience && (
              <span className="wellness-article__badge wellness-article__badge--audience">
                {post.audience}
              </span>
            )}
            <span>{formatBlogDate(post.publishedAt)}</span>
            <span>By {post.authorName}</span>
            {post.clubName && <span>{post.clubName}</span>}
          </p>
          <h1>{post.title}</h1>
          <p className="wellness-article__excerpt">{post.excerpt}</p>
          {isBoatRacePost(post) && <BoatRaceLanguageSwitcher post={post} />}
        </div>
      </header>

      <div className="wellness-article__shell">
        {post.sections.map((section, index) => renderWellnessSection(section, index))}

        {post.companionPost && (
          <section className="wellness-article__panel wellness-article__related">
            <h2>Go with this plan</h2>
            <p>
              Pair this article with{' '}
              <Link to={`/blog/${post.companionPost.slug}`}>{post.companionPost.title}</Link>
              {post.companionPost.blurb ? ` — ${post.companionPost.blurb}` : ''}
            </p>
          </section>
        )}

        {post.relatedPosts?.length > 0 && (
          <section className="wellness-article__panel wellness-article__related">
            <h2>{post.relatedPostsTitle || 'Related articles'}</h2>
            <ul>
              {post.relatedPosts.map((related) => (
                <li key={related.slug}>
                  <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                  {related.blurb ? ` — ${related.blurb}` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="wellness-article__panel wellness-article__share">
          <BlogShareBar post={post} />
        </section>

        {post.referenceLinks?.length > 0 && (
          <section className="wellness-article__panel">
            <h2>Related links</h2>
            <ul>
              {post.referenceLinks.map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="wellness-article__legal-footer">
          <button
            type="button"
            className="wellness-article__legal-link"
            onClick={() => setDisclaimerOpen(true)}
          >
            Medical &amp; legal disclaimer
          </button>
        </footer>

        <WellnessLegalDisclaimerModal
          open={disclaimerOpen}
          onClose={() => setDisclaimerOpen(false)}
        />
      </div>
    </article>
  );
};

export default WellnessGuideBlogArticle;
